import React, { useState, useEffect } from 'react';
import "bootstrap-icons/font/bootstrap-icons.css";
import { showConfirm } from '../../ConfirmBox';
import { IMG_BASE_URL } from '../../Helpers';
import {
    fetchBanners,
    addBanner,
    deleteBanner
} from '../../apiCalls';

export default function Banner() {
    const [banners, setBanners] = useState([]);
    const [loadingId, setLoadingId] = useState(null); // Track loader for specific rows
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newFile, setNewFile] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const MAX_BANNERS = 10;

    const loadBanners = async () => {
        try {
            const res = await fetchBanners();
            setBanners(Array.isArray(res) ? res : []);
            console.log("res", res)
        } catch (error) {
            console.error("Error loading banners:", error);
            setBanners([]);
        }
    };

    useEffect(() => {
        loadBanners();
        console.log("banners", banners)
    }, []);

    // Handle File Input selection change for individual rows
    const handleRowFileChange = (e, index, isNewRow = false) => {
        const file = e.target.files[0];
        setErrorMessage('');

        if (file && !file.type.startsWith('image/')) {
            setErrorMessage('Please choose a valid graphic format (PNG, JPG, WEBP).');
            return;
        }

        if (isNewRow) {
            setNewFile(file);
        } else {
            const updatedBanners = [...banners];
            updatedBanners[index].pendingFile = file;
            setBanners(updatedBanners);
        }
    };

    // Save/Update an existing banner row with a newly selected file
    const handleUpdateRow = async (index, id) => {
        const bannerRow = banners[index];
        if (!bannerRow.pendingFile) return;

        setLoadingId(id);
        try {
            const formData = new FormData();
            formData.append('image_file', bannerRow.pendingFile);

            // Assuming your backend update replacement routing pattern is delete old + post, 
            // or a single POST route that handles updating specific IDs:
            await deleteBanner(id);
            const res = await addBanner(formData);

            if (res && res.status === false) {
                setErrorMessage(res.message || 'Failed to update image.');
            } else {
                loadBanners();
            }
        } catch (error) {
            console.error("Update error:", error);
        } finally {
            setLoadingId(null);
        }
    };

    // Save a brand new banner row allocation slot
    const handleSaveNewRow = async () => {
        if (!newFile) {
            setErrorMessage('Please choose an image file first before clicking save.');
            return;
        }

        setLoadingId('new');
        try {
            const formData = new FormData();
            formData.append('image_file', newFile);

            const res = await addBanner(formData);
            if (res && res.status === false) {
                setErrorMessage(res.message || 'Failed to upload image.');
            } else {
                setNewFile(null);
                setIsAddingNew(false);
                loadBanners();
            }
        } catch (error) {
            console.error("Upload error:", error);
        } finally {
            setLoadingId(null);
        }
    };

    const handleDeleteRow = (id) => {
        console.log(id)
        showConfirm("Confirm Delete", "Are you sure you want to completely remove this banner image slot?", async () => {
            try {
                await deleteBanner(id);
                setErrorMessage('');
                loadBanners();
            } catch (error) {
                console.error("Delete error:", error);
            }
        });
    };

    return (
        <div className="container-fluid py-3">
            <div className="page-title-container d-flex justify-content-between align-items-center mb-4">
                <h2>
                    Home Page Banner
                    <span className="fs-6 text-muted ms-2">
                        ({banners.length} / {MAX_BANNERS} Slots Used)
                    </span>
                </h2>

                {banners.length < MAX_BANNERS ? (
                    !isAddingNew && (
                        <button className="btn btn-secondary btn-sm save-btn px-3" onClick={() => setIsAddingNew(true)}>
                            <i className="bi bi-plus-lg me-1"></i> Add Banner Row
                        </button>
                    )
                ) : (
                    <span className="badge bg-dark p-2 fs-7 shadow-sm">
                        <i className="bi bi-lock-fill me-1"></i> Max Capacity (10/10) Reached
                    </span>
                )}
            </div>

            {errorMessage && (
                <div className="alert alert-danger border-0 shadow-sm mb-4" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i> {errorMessage}
                </div>
            )}

            {/* --- INLINE EDITABLE BANNER MANAGEMENT LIST --- */}
            <div className="card border-0 shadow-sm p-3 bg-white">

                {banners.map((row, index) => (
                    <div className="row align-items-center border-bottom py-3 g-3" key={row.header_id}>

                        {/* 1. Left Side Image Preview */}
                        <div className="col-md-2 col-sm-3 text-center text-sm-start">
                            <span className="badge bg-light text-dark border mb-2 d-block w-fit-content">Slot {index + 1}</span>
                            <img
                                src={`${IMG_BASE_URL}banner/${row.image}`}
                                alt={`Banner ${index + 1}`}
                                className="img-thumbnail rounded shadow-sm"
                                style={{ maxWidth: "100%", maxHeight: "85px", objectFit: "cover", aspectRatio: "16/9" }}
                            />
                        </div>

                        {/* 2. File Upload Box Field Selector */}
                        <div className="col-md-6 col-sm-5">
                            <label className="form-label small text-muted mb-1 fw-bold">Replace Layout Graphic Asset</label>
                            <input
                                type="file"
                                className="form-control form-control-sm"
                                accept="image/*"
                                onChange={(e) => handleRowFileChange(e, index, false)}
                            />
                        </div>

                        {/* 3. Operational Action Triggers */}
                        <div className="col-md-4 col-sm-4 text-end">
                            <div className="d-flex gap-2 justify-content-sm-end justify-content-center pt-3 pt-sm-0">
                                <button
                                    className="btn btn-secondary btn-sm save-btn px-3 d-flex align-items-center gap-1"
                                    onClick={() => handleUpdateRow(index, row.header_id)}
                                    disabled={!row.pendingFile || loadingId === row.header_id}
                                >
                                    {loadingId === row.header_id ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-circle-fill"></i> Save
                                        </>
                                    )}
                                </button>
                                <button
                                    className="btn btn-outline-danger btn-sm px-3 d-flex align-items-center gap-1"
                                    onClick={() => handleDeleteRow(row.header_id)}
                                    disabled={loadingId !== null}
                                >
                                    <i className="bi bi-trash"></i> Delete
                                </button>
                            </div>
                        </div>

                    </div>
                ))}

                {/* --- DINAMIC ADD NEW ROW DOCK --- */}
                {isAddingNew && (
                    <div className="row align-items-center bg-light border border-secondary border-opacity-20 rounded p-3 my-2 g-3 animate-fade-in">

                        {/* 1. Placeholder preview container for client-uploaded streaming asset */}
                        <div className="col-md-2 col-sm-3 text-center text-sm-start">
                            <span className="badge bg-warning text-dark mb-2 d-block w-fit-content">New Row</span>
                            {newFile ? (
                                <img
                                    src={URL.createObjectURL(newFile)}
                                    alt="New Draft Preview"
                                    className="img-thumbnail rounded border-warning shadow-sm"
                                    style={{ maxWidth: "100%", maxHeight: "85px", objectFit: "cover", aspectRatio: "16/9" }}
                                />
                            ) : (
                                <div className="border border-dashed rounded d-flex align-items-center justify-content-center bg-white text-muted" style={{ height: "65px", aspectRatio: "16/9", fontSize: "0.75rem" }}>
                                    No File Selected
                                </div>
                            )}
                        </div>

                        {/* 2. Target input field setup */}
                        <div className="col-md-6 col-sm-5">
                            <label className="form-label small text-dark mb-1 fw-bold">Upload Source Graphic File <span className="text-danger">*</span></label>
                            <input
                                type="file"
                                className="form-control form-control-sm border-secondary"
                                accept="image/*"
                                onChange={(e) => handleRowFileChange(e, null, true)}
                            />
                        </div>

                        {/* 3. New Record Controls */}
                        <div className="col-md-4 col-sm-4 text-end">
                            <div className="d-flex gap-2 justify-content-sm-end justify-content-center">
                                <button
                                    className="btn btn-success btn-sm px-3 d-flex align-items-center gap-1 shadow-sm"
                                    onClick={handleSaveNewRow}
                                    disabled={!newFile || loadingId === 'new'}
                                >
                                    {loadingId === 'new' ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <>
                                            <i className="bi bi-cloud-upload-fill"></i> Save
                                        </>
                                    )}
                                </button>
                                <button
                                    className="btn btn-outline-secondary btn-sm px-3"
                                    onClick={() => { setIsAddingNew(false); setNewFile(null); }}
                                    disabled={loadingId !== null}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                    </div>
                )}

                {/* Empty Array Fallback Safety Hook */}
                {banners.length === 0 && !isAddingNew && (
                    <div className="text-center py-5 text-muted">
                        <i className="bi bi-images fs-1 d-block mb-2 text-opacity-20"></i>
                        <p className="mb-0">No active homepage banner entries found. Click "Add Banner Row" to start.</p>
                    </div>
                )}

            </div>
        </div>
    );
}